import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as decisionsApi from '../services/decisions-api.js';
import type { DecisionRow } from '../services/decisions-api.js';
import * as copilotApi from '../services/copilot-api.js';
import * as companyApi from '../services/company-api.js';
import '../components/copilot/cp-copilot-panel.js';

interface CompanyLite { id: number; name: string }

/**
 * Decisions-Sicht (compass-new Layer 3) — read-only. Events sind immutable; Tuning läuft
 * über den Goal-Tree-Editor. Zeigt Route, Pillar, Kind, Score, Grund je Decision.
 */
@customElement('cp-page-decisions')
export class CpPageDecisions extends LitElement {
  createRenderRoot() { return this; }

  @state() private _rows: DecisionRow[] = [];
  @state() private _companies: CompanyLite[] = [];
  @state() private _loading = false;
  @state() private _error = '';

  override connectedCallback(): void {
    super.connectedCallback();
    void this._load();
    void this._loadCompanies();
  }

  private async _loadCompanies(): Promise<void> {
    try { this._companies = (await companyApi.listCompanies()) as unknown as CompanyLite[]; } catch { /* ignore */ }
  }

  private async _load(): Promise<void> {
    this._loading = true; this._error = '';
    try { this._rows = await decisionsApi.listDecisions(); }
    catch (e) { this._error = e instanceof Error ? e.message : 'Fehler'; }
    finally { this._loading = false; }
  }

  private _companyName(id: number | null): string {
    if (id == null) return '—';
    const c = this._companies.find((x) => x.id === Number(id));
    return c ? c.name : `Firma #${id}`;
  }

  private _color(route: string): string {
    return route === 'new-substrategy' ? '#2e7d32' : route === 'deferred' ? '#b8860b' : route === 'ignore+log' ? '#888' : '#bbb';
  }

  private _routeLabel(route: string): string {
    return route === 'new-substrategy' ? 'Relevant' : route === 'deferred' ? 'Unklar' : route === 'ignore+log' ? 'Ignoriert' : route;
  }

  override render() {
    return html`
      <style>
        .dec-page { padding: 1.5rem; max-width: 1500px; margin: 0 auto; display: grid; grid-template-columns: 1fr 380px; gap: 1.5rem; align-items: start; }
        .dec-copilot { position: sticky; top: 1rem; height: calc(100vh - 6rem); }
        @media (max-width: 1100px) { .dec-page { grid-template-columns: 1fr; } .dec-copilot { position: static; height: 520px; } }
        .dec-wrap { font-family: var(--font-family-sans); color: var(--color-text-primary); min-width: 0; }
        .dec-head { display: flex; align-items: baseline; gap: 1rem; margin-bottom: 1rem; }
        .dec-head h1 { font-size: 1.4rem; margin: 0; color: var(--color-text-primary); }
        .dec-sub { color: var(--color-text-secondary); font-size: .9rem; }
        .dec-refresh { margin-left: auto; cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 6px; padding: .3rem .7rem; }
        .dec-refresh:hover { border-color: var(--color-border-hover); background: var(--color-bg-hover); }
        table.dec { width: 100%; border-collapse: collapse; font-size: .88rem; }
        table.dec th, table.dec td { text-align: left; padding: .5rem .6rem; border-bottom: 1px solid var(--color-border); vertical-align: top; }
        table.dec th { color: var(--color-text-secondary); font-weight: 600; }
        table.dec td { color: var(--color-text-primary); }
        .dec-route { font-weight: 600; }
        .dec-muted { color: var(--color-text-muted); font-size: .8rem; }
      </style>
      <div class="dec-page">
      <div class="dec-wrap">
        <div class="dec-head">
          <h1>Decisions</h1>
          <span class="dec-sub">${this._rows.length} Decisions · immutable (read-only)</span>
          <button class="dec-refresh" @click=${() => void this._load()}>↻ Neu laden</button>
        </div>
        <p class="dec-muted" style="margin:.2rem 0 1rem; line-height:1.5;">
          Eine Decision pro Signal: gegen welches Ziel (Pillar) welcher Firma es geprüft wurde und das Ergebnis —
          <strong>Relevant</strong> (Teilstrategie), <strong>Unklar</strong> (Constraint fehlt), <strong>Ignoriert</strong>.
          Immutable: getunt wird über den Goal-Tree (Ziele), nicht hier.
        </p>
        ${this._error ? html`<p style="color:#c33">${this._error}</p>` : nothing}
        ${this._loading ? html`<p>Lädt…</p>` : nothing}
        ${!this._loading && this._rows.length === 0
          ? html`<p class="dec-muted">Noch keine Decisions. Erst Signale abrufen (Signale-Seite → Quellen).</p>`
          : html`
          <table class="dec">
            <thead><tr><th>Entscheidung</th><th>Ziel (Pillar)</th><th>Art</th><th>Firma</th><th>Score</th><th>Grund / fehlt</th><th>Zeit</th></tr></thead>
            <tbody>
              ${this._rows.map((d) => html`
                <tr>
                  <td><span class="dec-route" style="color:${this._color(d.route)}">${this._routeLabel(d.route)}</span></td>
                  <td>${d.pillar ?? '—'}</td>
                  <td>${d.kind ?? '—'}</td>
                  <td>${this._companyName(d.companyId)}</td>
                  <td>${d.score ?? '—'}</td>
                  <td>${d.reason ?? ''}${d.missing && d.missing.length ? html`<span class="dec-muted"> ${d.missing.join(', ')}</span>` : nothing}</td>
                  <td class="dec-muted">${d.occurredAt}</td>
                </tr>
              `)}
            </tbody>
          </table>`}
      </div>
      <aside class="dec-copilot">
        <cp-copilot-panel
          heading="Decision-Copilot"
          subtitle="Versteh, warum eine Decision so ausfiel — und was zu tunen wäre."
          placeholder="z.B. Warum landen Staffing-Signale auf deferred?"
          example="Welche Decisions sind deferred und welcher Constraint/Pillar fehlt dafür?"
          .send=${(m: string) => copilotApi.askDecisions(m)}
        ></cp-copilot-panel>
      </aside>
      </div>
    `;
  }
}
